import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { DEFAULT_COUNTRY } from '@/utils/constants.js';
import { createQuestionObj } from '@/utils/helpers.js';
import { isValidPostalCode } from '@/utils/localeUtils.js';

import { useBookingStore, usePersistedPrismicStore } from '@/store.js';

import { DropDown } from '../Common/DropDown/DropDown.jsx';
import TextfieldQuestion from '../CustomAnamnesis/QuestionTypes/TextfieldQuestion.jsx';
import EternoSpinner from '../EternoLogoSpinner/eterno-spinner.tsx';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        position: 'relative',
        '& [data-testid^="question-"]': {
            border: 'none',
            padding: 0,
        },
    },
    questionGroupContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1rem',
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
        },
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
    },
}));

const Address = () => {
    const classes = useStyles();
    const theme = useTheme();
    const { i18n } = useTranslation();
    const matchesXs = useMediaQuery(theme.breakpoints.down('xs'));
    const {
        [i18n.language]: { content: prismicData },
    } = usePersistedPrismicStore((state) => state.accountData);
    const {
        street,
        houseNumber,
        zipCode,
        city,
        country: storedCountry,
    } = useBookingStore((state) => state.registrationFormData);
    // Autofill and going back can leave this unset, and an unset country used to crash the step.
    const country = storedCountry || DEFAULT_COUNTRY;
    const { loading, setStreet, setHouseNumber, setZipCode, setCity, setCountry } = useBookingStore(
        useShallow((state) => ({
            loading: state.loading,
            setStreet: state.setStreet,
            setHouseNumber: state.setHouseNumber,
            setZipCode: state.setZipCode,
            setCity: state.setCity,
            setCountry: state.setCountry,
        }))
    );

    return (
        <div className={classes.container}>
            {loading && (
                <div className={classes.loadingOverlay}>
                    <EternoSpinner />
                </div>
            )}
            <DropDown
                type="country"
                mainDataHeading={prismicData.country[0].text}
                setData={setCountry}
                data={country.toUpperCase()}
                noMarginTop
                required
            />
            <div className={classes.questionGroupContainer}>
                <TextfieldQuestion
                    question={createQuestionObj('', prismicData.street[0].text, 'street')}
                    answer={street}
                    setAnswer={(value) => setStreet(value)}
                    hideTitle
                    required
                />
                <div style={{ width: matchesXs ? '100%' : 'auto' }}>
                    <TextfieldQuestion
                        question={createQuestionObj('', prismicData?.street_number?.[0]?.text, 'houseNumber')}
                        answer={houseNumber}
                        setAnswer={(value) => setHouseNumber(value)}
                        hideTitle
                        required
                    />
                </div>
            </div>
            <div className={classes.questionGroupContainer}>
                <div style={{ width: matchesXs ? '100%' : 'auto' }}>
                    <TextfieldQuestion
                        question={createQuestionObj('', prismicData.zip_code_label[0].text, 'zipcode')}
                        autoComplete="postal-code"
                        answer={zipCode}
                        setAnswer={(value) => setZipCode(value)}
                        hideTitle
                        customValidation={(value) => isValidPostalCode(value, country)}
                        customValidationErrorMessage={prismicData.zip_code_incorrect[0].text}
                        required
                    />
                </div>
                <TextfieldQuestion
                    question={createQuestionObj('', prismicData.city_label[0].text, 'city')}
                    autoComplete="address-level2"
                    answer={city}
                    setAnswer={(value) => setCity(value)}
                    hideTitle
                    required
                />
            </div>
        </div>
    );
};
export default Address;
